// Microsoft Graph API helpers — Outlook drafts + inbox reads + (future) OneDrive files.
//
// Client-side only — uses the signed-in user's access token from MSAL. The
// Worker doesn't need to know about Graph for the M1.5 path; future watchers
// that need to act when no user is logged in would change that.
//
// HARD PROJECT RULE — drafts NEVER auto-send. Per
// [[feedback-drafts-never-autosend]], every outbound-comm helper in this file
// creates a Draft in the user's Outlook only. Justin opens Outlook, reviews,
// and clicks Send himself. There is intentionally no helper that calls
// /me/sendMail or /me/messages/{id}/send. Don't add one.

import { getAccessToken } from './auth';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

// =====================================================
// Shared — error mapping for friendly 401 / 403 messages
// =====================================================
//
// Graph returns:
//   401 — token expired / invalid (auth.ts will already have tried silent
//         re-acquisition once; if we still get 401 the user truly needs to
//         re-auth). Surface a sign-in-again message.
//   403 — token is valid but the consented scope set lacks the right
//         permission. This is almost always "Mail.Read / Mail.ReadWrite was
//         added in code but never granted in Azure". Tell the user exactly
//         where to click.
// Anything else we pass through verbatim so the actual Graph error message
// makes it to the dev console.

async function throwFriendly(res: Response, op: string, scopeHint: string): Promise<never> {
  // Try to extract the Graph error body; ignore JSON parse failures.
  const data = (await res.json().catch(() => ({}))) as { error?: { message?: string; code?: string } };
  const upstream = data.error?.message ?? res.statusText;

  if (res.status === 401) {
    throw new Error(
      `Microsoft sign-in expired while ${op}. Sign in with Microsoft again.`,
    );
  }
  if (res.status === 403) {
    throw new Error(
      `Microsoft Graph rejected ${op} — missing ${scopeHint} permission. ` +
        `Open Azure → App registrations → ai-quote SPA → API permissions and add ` +
        `${scopeHint} (Delegated). Then click "Grant admin consent" or accept the ` +
        `consent prompt on next sign-in. Graph said: ${upstream}`,
    );
  }
  throw new Error(`Graph API ${res.status} while ${op}: ${upstream}`);
}

// =====================================================
// Outlook — drafts
// =====================================================

export interface OutlookDraftParams {
  subject: string;
  body: string;
  bodyType?: 'HTML' | 'Text';
  to?: string;
  cc?: string;
  bcc?: string;
}

export interface OutlookDraft {
  id: string;
  webLink: string;
  subject: string;
  createdDateTime: string;
}

function parseRecipients(s: string | undefined): Array<{ emailAddress: { address: string } }> | undefined {
  if (!s) return undefined;
  const list = s
    .split(/[;,]/)
    .map((a) => a.trim())
    .filter(Boolean)
    .map((a) => ({ emailAddress: { address: a } }));
  return list.length ? list : undefined;
}

/**
 * Create a draft email in the signed-in user's Outlook Drafts folder.
 * Returns the draft ID and the webLink (URL to open the draft directly in OWA).
 *
 * Per [[feedback-drafts-never-autosend]] — this method NEVER sends. It only
 * creates a draft. The user opens Outlook, reviews, edits, and clicks Send.
 */
export async function saveToOutlookDrafts(params: OutlookDraftParams): Promise<OutlookDraft> {
  if (!params.subject && !params.body) {
    throw new Error('Either subject or body is required to create a draft.');
  }

  const token = await getAccessToken();

  const payload: Record<string, unknown> = {
    subject: params.subject ?? '',
    body: {
      contentType: params.bodyType ?? 'Text',
      content: params.body ?? '',
    },
  };

  const to = parseRecipients(params.to);
  if (to) payload.toRecipients = to;
  const cc = parseRecipients(params.cc);
  if (cc) payload.ccRecipients = cc;
  const bcc = parseRecipients(params.bcc);
  if (bcc) payload.bccRecipients = bcc;

  const res = await fetch(`${GRAPH_BASE}/me/messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    await throwFriendly(res, 'saving Outlook draft', 'Mail.ReadWrite');
  }

  const data = (await res.json()) as OutlookDraft;
  return {
    id: data.id,
    webLink: data.webLink,
    subject: data.subject,
    createdDateTime: data.createdDateTime,
  };
}

// =====================================================
// Outlook — read (inbox + single message)
// =====================================================

export interface OutlookMessageSummary {
  id: string;
  subject: string;
  preview: string;          // bodyPreview from Graph (~255 chars, plain text)
  from: { name: string; email: string };
  receivedDateTime: string; // ISO 8601
  isRead: boolean;
  hasAttachments: boolean;
  webLink: string;          // direct URL to open in Outlook on the web
}

// Graph's raw message shape — we only pluck what we expose.
interface GraphMessageRaw {
  id: string;
  subject?: string;
  bodyPreview?: string;
  from?: { emailAddress?: { name?: string; address?: string } };
  receivedDateTime?: string;
  isRead?: boolean;
  hasAttachments?: boolean;
  webLink?: string;
}

function mapMessageSummary(m: GraphMessageRaw): OutlookMessageSummary {
  return {
    id: m.id,
    subject: m.subject ?? '(no subject)',
    preview: m.bodyPreview ?? '',
    from: {
      name: m.from?.emailAddress?.name ?? '',
      email: m.from?.emailAddress?.address ?? '',
    },
    receivedDateTime: m.receivedDateTime ?? '',
    isRead: m.isRead ?? false,
    hasAttachments: m.hasAttachments ?? false,
    webLink: m.webLink ?? '',
  };
}

/**
 * Read the user's inbox (or sent items). Defaults to the 25 most recent.
 *
 * Requires Mail.Read scope. If `search` is provided we use Graph's $search
 * KQL syntax — note Graph forbids combining $search with $orderby, so when
 * searching we don't pass an explicit sort (Graph ranks by relevance).
 *
 * Per the drafts-never-autosend rule, this is read-only. No reply / forward
 * side effects.
 */
export async function readInbox(opts?: {
  top?: number;
  folder?: 'inbox' | 'sentitems';
  search?: string;
}): Promise<OutlookMessageSummary[]> {
  const top = Math.max(1, Math.min(opts?.top ?? 25, 100)); // Graph caps $top at 1000, but 100 is sane
  const folder = opts?.folder ?? 'inbox';
  const token = await getAccessToken();

  // Only pull the fields we expose — keeps response small + cheap.
  const select = [
    'id',
    'subject',
    'bodyPreview',
    'from',
    'receivedDateTime',
    'isRead',
    'hasAttachments',
    'webLink',
  ].join(',');

  const params = new URLSearchParams({ $top: String(top), $select: select });
  if (opts?.search) {
    // $search uses KQL — quote-wrap to be safe with whitespace
    params.set('$search', `"${opts.search.replace(/"/g, '\\"')}"`);
    // Don't add $orderby when searching — Graph rejects the combo.
  } else {
    params.set('$orderby', 'receivedDateTime desc');
  }

  const url = `${GRAPH_BASE}/me/mailFolders/${folder}/messages?${params.toString()}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      // ConsistencyLevel: eventual is required by Graph when $search is used.
      // Harmless on non-search calls, so set it unconditionally.
      ConsistencyLevel: 'eventual',
    },
  });

  if (!res.ok) {
    await throwFriendly(res, 'reading inbox', 'Mail.Read');
  }

  const data = (await res.json()) as { value?: GraphMessageRaw[] };
  return (data.value ?? []).map(mapMessageSummary);
}

/**
 * Fetch a single message body. Returns the HTML body so the caller can render
 * it richly. Use sparingly — full bodies are large; prefer `readInbox` previews
 * for triage and only `readMessage` when you need the full content.
 *
 * Requires Mail.Read scope.
 */
export async function readMessage(id: string): Promise<{
  subject: string;
  from: OutlookMessageSummary['from'];
  bodyHtml: string;
  receivedDateTime: string;
  toRecipients: Array<{ name: string; email: string }>;
}> {
  const token = await getAccessToken();
  const select = ['subject', 'from', 'body', 'receivedDateTime', 'toRecipients'].join(',');
  const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(id)}?$select=${select}`;

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    await throwFriendly(res, 'reading message', 'Mail.Read');
  }

  interface GraphFullMessage {
    subject?: string;
    from?: { emailAddress?: { name?: string; address?: string } };
    body?: { contentType?: string; content?: string };
    receivedDateTime?: string;
    toRecipients?: Array<{ emailAddress?: { name?: string; address?: string } }>;
  }
  const data = (await res.json()) as GraphFullMessage;

  return {
    subject: data.subject ?? '(no subject)',
    from: {
      name: data.from?.emailAddress?.name ?? '',
      email: data.from?.emailAddress?.address ?? '',
    },
    // Graph returns HTML by default unless requested otherwise; if a sender
    // used plain text Graph still wraps it as HTML for this endpoint.
    bodyHtml: data.body?.content ?? '',
    receivedDateTime: data.receivedDateTime ?? '',
    toRecipients: (data.toRecipients ?? []).map((r) => ({
      name: r.emailAddress?.name ?? '',
      email: r.emailAddress?.address ?? '',
    })),
  };
}

// =====================================================
// Outlook — draft new emails + draft replies (NEVER sends)
// =====================================================

/**
 * Normalize a recipient (string-or-object) into Graph's `{ emailAddress: { ... } }` shape.
 */
function toGraphRecipient(
  r: string | { name?: string; email: string },
): { emailAddress: { address: string; name?: string } } {
  if (typeof r === 'string') return { emailAddress: { address: r } };
  return { emailAddress: { address: r.email, name: r.name } };
}

/**
 * Draft a new email or a reply. ALWAYS saves to Outlook → Drafts; NEVER sends.
 * Per [[feedback-drafts-never-autosend]] this is the contract — Justin reviews
 * every outbound comm before it leaves the building.
 *
 * - **New email**: omit `replyToMessageId`. We POST `/me/messages` (same path
 *   saveToOutlookDrafts uses) which creates a brand-new draft.
 * - **Reply / replyAll**: pass `replyToMessageId`. We call
 *   `/me/messages/{id}/createReply` which makes a draft pre-populated with the
 *   quoted-original; we then PATCH our subject/body/recipients onto it.
 *
 * Requires Mail.ReadWrite scope.
 */
export async function draftEmail(opts: {
  to: Array<string | { name?: string; email: string }>;
  cc?: Array<string | { name?: string; email: string }>;
  subject: string;
  bodyHtml: string;
  replyToMessageId?: string;
}): Promise<{ draftId: string; webLink: string }> {
  if (!opts.to || opts.to.length === 0) {
    throw new Error('draftEmail requires at least one recipient in `to`.');
  }
  const token = await getAccessToken();
  const toRecipients = opts.to.map(toGraphRecipient);
  const ccRecipients = opts.cc?.map(toGraphRecipient);

  let draftId: string;
  let webLink: string;

  if (opts.replyToMessageId) {
    // Step 1 — let Graph create the reply-draft with proper threading + quoted body.
    const createRes = await fetch(
      `${GRAPH_BASE}/me/messages/${encodeURIComponent(opts.replyToMessageId)}/createReply`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // No body needed; Graph copies the original message into the draft.
        body: '{}',
      },
    );
    if (!createRes.ok) {
      await throwFriendly(createRes, 'creating reply draft', 'Mail.ReadWrite');
    }
    const created = (await createRes.json()) as { id: string; webLink: string };
    draftId = created.id;
    webLink = created.webLink;

    // Step 2 — PATCH our subject/body/recipients onto the draft. Graph's
    // createReply pre-fills subject ("Re: …") and toRecipients with the
    // original sender; we override with what the caller actually asked for.
    const patchPayload: Record<string, unknown> = {
      subject: opts.subject,
      body: { contentType: 'HTML', content: opts.bodyHtml },
      toRecipients,
    };
    if (ccRecipients) patchPayload.ccRecipients = ccRecipients;

    const patchRes = await fetch(`${GRAPH_BASE}/me/messages/${encodeURIComponent(draftId)}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patchPayload),
    });
    if (!patchRes.ok) {
      await throwFriendly(patchRes, 'updating reply draft', 'Mail.ReadWrite');
    }
    // Re-fetch webLink in case PATCH changed it (it doesn't today, but be safe).
    const patched = (await patchRes.json()) as { webLink?: string };
    if (patched.webLink) webLink = patched.webLink;
  } else {
    // Brand-new draft. POST /me/messages creates it in the Drafts folder.
    const payload: Record<string, unknown> = {
      subject: opts.subject,
      body: { contentType: 'HTML', content: opts.bodyHtml },
      toRecipients,
    };
    if (ccRecipients) payload.ccRecipients = ccRecipients;

    const res = await fetch(`${GRAPH_BASE}/me/messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      await throwFriendly(res, 'creating draft', 'Mail.ReadWrite');
    }
    const created = (await res.json()) as { id: string; webLink: string };
    draftId = created.id;
    webLink = created.webLink;
  }

  return { draftId, webLink };
}

// =====================================================
// Me — basic profile
// =====================================================

export interface GraphMe {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
}

export async function getMe(): Promise<GraphMe> {
  const token = await getAccessToken();
  const res = await fetch(`${GRAPH_BASE}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Graph /me ${res.status}: ${res.statusText}`);
  return (await res.json()) as GraphMe;
}
